import { useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CourseLite } from "../../types/course.response";
import { getCourseKey } from "../../utils/courseUtils";
import TrashIcon from "../icons/TrashIcon";
import Loading from "../loading/Loading";
import UnappliedWarning from "../shared/UnappliedWarning";

interface ChecklistCourseRowProps {
    course: CourseLite;
    isApplied: boolean;
    nodeId: string;
    requirementId: string;
    onDelete: (course: CourseLite) => void | Promise<void>;
}

function getStatusColor(course: CourseLite, isApplied: boolean) {
    const status = course.userState.status;
    if (isApplied) {
        if (status === "COMPLETED") return "bg-green-500";
        if (status === "IN_PROGRESS") return "bg-amber-400";
        if (status === "PLANNED") return "bg-blue-500";
        return "bg-gray-300";
    }
    if (status === "COMPLETED") return "bg-transparent border-2 border-green-400";
    if (status === "IN_PROGRESS") return "bg-transparent border-2 border-amber-400";
    if (status === "PLANNED") return "bg-transparent border-2 border-blue-400";
    return "bg-gray-300";
}

export default function ChecklistCourseRow({ course, isApplied, nodeId, requirementId, onDelete }: ChecklistCourseRowProps) {
    const [isDeleting, setIsDeleting] = useState(false);
    const unappliedBinding = !isApplied
        ? course.userState.requirementBindings.find(
              b => b.requirementId === requirementId && (b.status === 'UNAPPLIED' || b.status === 'FORCE_UNAPPLIED')
          )
        : undefined;
    const courseKey = getCourseKey(course);

    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
        id: `checklist-drag-${courseKey}`,
        data: { course, isApplied, nodeId },
    });

    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: `checklist-drop-${courseKey}`,
        data: { course, nodeId, type: "chip" },
        disabled: !isApplied,
    });

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDeleting(true);
        try {
            await onDelete(course);
        } finally {
            setIsDeleting(false);
        }
    };

    const setRefs = (el: HTMLElement | null) => {
        setDragRef(el);
        setDropRef(el);
    };

    return (
        <div
            ref={setRefs}
            {...listeners}
            {...attributes}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md group transition-all cursor-grab active:cursor-grabbing
                ${isDragging ? "opacity-40 scale-[0.98]" : "hover:bg-gray-50"}
                ${isOver && isApplied ? "ring-2 ring-blue-400 ring-offset-1" : ""}
                ${isDeleting ? "opacity-50" : ""}
            `}
        >
            <div className="flex-shrink-0 text-gray-300 group-hover:text-gray-400 transition-colors">
                <div className="grid grid-cols-2 gap-[2px]">
                    <div className="w-[3px] h-[3px] bg-current rounded-full" />
                    <div className="w-[3px] h-[3px] bg-current rounded-full" />
                    <div className="w-[3px] h-[3px] bg-current rounded-full" />
                    <div className="w-[3px] h-[3px] bg-current rounded-full" />
                </div>
            </div>
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(course, isApplied)}`} />
            <div className="flex-1 min-w-0 md:flex md:items-center md:gap-2.5">
                <span className="whitespace-nowrap text-sm md:text-base md:text-inherit md:w-28 md:flex-shrink-0">
                    {course.courseInfo.subject} {course.courseInfo.number}
                </span>
                <span className="block md:inline text-sm md:text-base text-gray-900 md:text-inherit truncate md:w-[32rem] md:flex-1 md:min-w-0">
                    {course.courseInfo.titleShort}{course.topic && ` - ${course.topic}`}
                </span>
            </div>
            {unappliedBinding && (
                <UnappliedWarning binding={unappliedBinding} iconClassName="w-3.5 h-3.5" />
            )}
            <button
                onClick={handleDelete}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={isDeleting}
                className="p-1 rounded opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity disabled:cursor-not-allowed flex-shrink-0"
            >
                {isDeleting ? <Loading size="sm" /> : <TrashIcon className="w-3.5 h-3.5" />}
            </button>
        </div>
    );
}
